import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import uniqueId from 'lodash/uniqueId';
import map from 'lodash/map';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import externalUrl from '@Utils/ExternalUrl';
import ChemquoteProviderListItem from '@Components/Results/structure/availability/ChemquoteProviderListItem';
import CommercialSubstancesProviderListItem from '@Components/Results/structure/availability/CommercialSubstancesProviderListItem';
import { LinkElement } from '@els/els-react--link-element';

import './AvailabilityProviders.scss';

function AvailabilityProviders({
  className,
  structure,
  closeMenu,
  onProviderClick = () => null,
  providers = [],
  loadImage,
  isOpen,
  providersArePreload,
  details,
}) {
  const [stateProviders, setProviders] = useState([]);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const spinnerId = useRef(uniqueId('shopping-cart-'));

  function loadProviders() {
    const promises = map(providers, loadImage);
    return Promise.all(promises)
      .then((loadedProviders) => {
        setProviders(loadedProviders);
        setProvidersLoaded(true);
      })
      .catch(Logger.error);
  }

  useEffect(() => {
    if (!providersArePreload) return;
    Spinners.activate(spinnerId.current);
    loadProviders().finally(() => Spinners.terminate(spinnerId.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!providersArePreload && isOpen) {
      Spinners.activate(spinnerId.current);
      loadProviders().finally(() => Spinners.terminate(spinnerId.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, providersArePreload]);

  function providerClickHandler(event, provider) {
    event.preventDefault();
    onProviderClick(provider.label);

    if (provider.linkUrl) {
      window.open(externalUrl(provider.linkUrl), '_blank');
    }
    closeMenu();
  }

  // Check if there are any visible providers
  const hasVisibleProviders = stateProviders.some((provider) => provider.show);

  return (
    <div className={cn('availability-providers', className)}>
      <div className="availability-providers--spinner-container">
        <RxSpinnerComponent spinnerId={spinnerId.current} spinnerClass="availability-providers--spinner" spinnerScale={1} />
      </div>
      {providersLoaded ? (
        <ul className="commercial-availability" role="menu">
          <CommercialSubstancesProviderListItem structure={structure} closeMenu={closeMenu} onClick={() => onProviderClick('rcs')} />
          <ShoppingCartTooltipContent
            details={details}
            className={hasVisibleProviders ? 'availability-tooltip-content' : 'without-provider'}
          />
          <ChemquoteProviderListItem
            closeMenu={closeMenu}
            providers={stateProviders}
            structure={structure}
            onClick={() => onProviderClick('chemquote')}
          />
          <ul aria-label="Top commercial suppliers">
            {stateProviders.map((provider, index) =>
              provider?.show ? (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} role="menuitem" className="availability-provider-list-item u-els-display-flex">
                  <span className="column-provider--logo">
                    <img className="provider-image" src={provider.imageUrl} alt={provider.label} />
                  </span>
                  <LinkElement
                    data-e2e="availability-provider-link"
                    className={cn('provider-link', `e2e-shopping-cart-${provider.label}`)}
                    href="#"
                    iconSprite="UpRight"
                    iconRight
                    iconSize="xs"
                    isExternal
                    onClick={(event) => providerClickHandler(event, provider)}>
                    {provider.label}
                  </LinkElement>
                </li>
              ) : null
            )}
          </ul>
        </ul>
      ) : null}
    </div>
  );
}

AvailabilityProviders.propTypes = {
  providers: PropTypes.array.isRequired,
  loadImage: PropTypes.func.isRequired,
  closeMenu: PropTypes.func.isRequired,
  structure: PropTypes.object.isRequired,
  className: PropTypes.string,
  onProviderClick: PropTypes.func,
  isOpen: PropTypes.bool,
  providersArePreload: PropTypes.bool,
  details: PropTypes.object,
};

export default AvailabilityProviders;
